// Brief 70 (24 Jun 2026): post-review action bar for the CCL signing
// pipeline. Replaces the old "CCL reviewed and confirmed" dead-end with a
// 3-control bar:
//   [ Apply Signature ▾ ]  [ Send to Client for Signing ]  [ Status: … ]
//
// - Apply Signature: lists fee earner signatures (no gating — any user can
//   apply any FE's sig; the backend audits actor + on-behalf-of). On apply
//   we show the chosen FE name + sig preview as visual confirmation.
// - Send to Client: disabled until a signature is applied. Opens a modal
//   (To / Subject / Body, all editable) and POSTs /signing/create.
// - Status: once an invite is sent, polls /signing/{token} every 10s.
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { PenLine, Send, Loader2, CheckCircle2, Clock, AlertTriangle, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { Button } from "../components/ui/button";
import { FIRM } from "../fixtures/firmIdentity";
import { defaultCoverBody } from "../utils/cclCoverEmail";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

export default function CclPostReviewActions({
  submissionId,
  submissionData = {},
  matterRef = "",
  clientName = "",
  // BM-H474 (9 Sep 2026): the signature the client already gave, as recorded
  // on the server and carried by GET /api/submissions/{id}. Before this the
  // component only knew about a signature it had watched happen in the current
  // browser session, so navigating back to the CCL page from the pipeline
  // offered "Send to Client for Signing" on a letter the client had signed.
  initialSigningStatus = null,
  initialSigningToken = null,
  // BM-H597: lets the editor hide Revert to draft the moment a signature
  // arrives by polling, and know that the letter has been sent.
  onSigningStatusChange,
}) {
  const { email: currentUserEmail } = useCurrentUser();
  const [signatures, setSignatures] = useState([]);
  const [applying, setApplying] = useState(false);
  const [appliedSig, setAppliedSig] = useState(null); // {email, name, signature_data}
  const [selectedEmail, setSelectedEmail] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const forfEmail =
    submissionData?.forf_email ||
    submissionData?.client_email ||
    submissionData?.data?.client_email ||
    submissionData?.data?.Q5 ||
    "";

  const [toEmail, setToEmail] = useState(forfEmail);
  const [subject, setSubject] = useState(
    matterRef
      ? `Please sign your Client Care Letter: ${matterRef}`
      : "Please sign your Client Care Letter"
  );
  const [salutation, setSalutation] = useState(
    clientName ? `Dear ${clientName}` : "Dear Client"
  );
  const [body, setBody] = useState(() => defaultCoverBody(FIRM.tradingName, submissionData));

  const [token, setToken] = useState(initialSigningToken);
  const [status, setStatus] = useState(initialSigningStatus); // pending | signed | failed

  // BM-H474: once the client has signed, the send action is gone for good.
  // Sending again is a duplicate engagement letter on a live matter.
  const clientSigned = status === "signed";

  useEffect(() => {
    onSigningStatusChange?.(status);
  }, [status, onSigningStatusChange]);

  // A later page load must not be able to lose a signature we were told about.
  useEffect(() => {
    if (initialSigningStatus === "signed") setStatus("signed");
  }, [initialSigningStatus]);
  const pollRef = useRef(null);

  // ── Load fee earner signatures (only those with a stored image), A→Z ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/signatures`);
        const list = Array.isArray(res.data) ? res.data : [];
        const withSig = list
          .filter((s) => s.has_signature)
          .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
        if (alive) setSignatures(withSig);
      } catch {
        if (alive) setSignatures([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Default selection: the logged-in user if they have a stored signature,
  // otherwise the first fee earner alphabetically. Lets a PA (e.g. Liezel)
  // still apply someone else's signature, while the common case (a fee
  // earner signing their own CCL) is pre-selected.
  const defaultEmail = useMemo(() => {
    if (!signatures.length) return "";
    const mine = signatures.find(
      (s) => s.email.toLowerCase() === (currentUserEmail || "").toLowerCase()
    );
    return mine ? mine.email : signatures[0].email;
  }, [signatures, currentUserEmail]);

  useEffect(() => {
    if (defaultEmail && !selectedEmail) setSelectedEmail(defaultEmail);
  }, [defaultEmail, selectedEmail]);

  const selectedSig = signatures.find((s) => s.email === selectedEmail) || null;

  // ── Apply a fee earner signature ──────────────────────────────────
  const handleApply = useCallback(async (sig) => {
    try {
      setApplying(true);
      setSelectedEmail(sig.email);
      const res = await axios.post(`${API}/signing/apply-fee-earner-signature`, {
        submission_id: submissionId,
        fee_earner_email: sig.email,
      });
      // Prefer the thumbnail already returned by GET /signatures; fall back to
      // a per-earner fetch if the list didn't carry the image.
      let signatureData = sig.signature_data || "";
      if (!signatureData) {
        try {
          const det = await axios.get(`${API}/signatures/${encodeURIComponent(sig.email)}`);
          signatureData = det.data?.signature_data || "";
        } catch { /* preview is best-effort */ }
      }
      setAppliedSig({
        email: sig.email,
        name: res.data?.fee_earner_name || sig.name || sig.email,
        signature_data: signatureData,
      });
      toast.success(`Signature applied: ${res.data?.fee_earner_name || sig.name}`);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to apply signature"));
    } finally {
      setApplying(false);
    }
  }, [submissionId]);

  // ── Poll signing status ───────────────────────────────────────────
  const pollStatus = useCallback(async (tok) => {
    try {
      const res = await axios.get(`${API}/signing/${tok}`);
      const st = res.data?.status || "pending";
      setStatus(st);
      if (st === "signed" && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch {
      setStatus("failed");
    }
  }, []);

  useEffect(() => {
    // BM-H474: a signature already recorded on the server needs no polling.
    if (!token || clientSigned) return undefined;
    pollStatus(token);
    pollRef.current = setInterval(() => pollStatus(token), 10000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [token, clientSigned, pollStatus]);

  // ── Send to client ────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    try {
      setSending(true);
      const res = await axios.post(`${API}/signing/create`, {
        submission_id: submissionId,
        client_email: toEmail,
        salutation,
        subject,
        email_body: body,
      });
      const perRecipient = res.data?.per_recipient || [];
      if (perRecipient.length) {
        perRecipient.forEach((r) => {
          const ok = ["sent", "success", "safe_mode"].includes(r.status);
          if (ok) toast.success(`Invite sent to ${r.email}`);
          else toast.error(`Failed to send to ${r.email}: ${r.error || r.status}`);
        });
      } else if (res.data?.success) {
        toast.success("Signing invite sent");
      } else {
        toast.error("Failed to send signing invite");
      }
      const tok = res.data?.main_token || null;
      if (tok) {
        setToken(tok);
        setStatus("pending");
      }
      setModalOpen(false);
    } catch (err) {
      // errorMessage unwraps a structured detail ({code, message, endpoint}),
      // e.g. the BM-H4 409 when EMAILS_ENABLED is off, into readable copy —
      // a raw detail object would render as "[object Object]".
      toast.error(errorMessage(err, "Failed to send signing invite"));
    } finally {
      setSending(false);
    }
  }, [submissionId, toEmail, salutation, subject, body]);

  const statusLabel = () => {
    // BM-H474: a recorded signature is shown whether or not this browser
    // session happens to hold the signing token.
    if (!token && !status) return null;
    if (status === "signed") {
      return (
        <span className="text-sm text-status-green flex items-center gap-1.5" data-testid="ccl-sign-status">
          <CheckCircle2 className="w-4 h-4" /> Signed by client
        </span>
      );
    }
    if (status === "failed") {
      return (
        <span className="text-sm text-red-600 flex items-center gap-1.5" data-testid="ccl-sign-status">
          <AlertTriangle className="w-4 h-4" /> Status unavailable
        </span>
      );
    }
    return (
      <span className="text-sm text-amber-600 flex items-center gap-1.5" data-testid="ccl-sign-status">
        <Clock className="w-4 h-4" /> Awaiting client signature
      </span>
    );
  };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="ccl-post-review-actions">
      {/* Apply Signature dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 border-teal text-teal hover:bg-teal/5"
            disabled={applying}
            data-testid="apply-signature-trigger"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
            {appliedSig
              ? `Signature: ${appliedSig.name}`
              : selectedSig
              ? `Apply Signature: ${selectedSig.name}`
              : "Apply Signature"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72" data-testid="apply-signature-menu">
          <DropdownMenuLabel>Fee earner signatures</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {signatures.length === 0 ? (
            <DropdownMenuItem disabled data-testid="apply-signature-empty">
              No stored signatures
            </DropdownMenuItem>
          ) : (
            signatures.map((sig) => (
              <DropdownMenuItem
                key={sig.email}
                onSelect={() => handleApply(sig)}
                className="gap-2"
                data-testid={`apply-signature-item-${sig.email}`}
              >
                <span className="w-4 flex-shrink-0">
                  {sig.email === selectedEmail && <Check className="w-4 h-4 text-teal" />}
                </span>
                {sig.signature_data ? (
                  <img
                    src={sig.signature_data}
                    alt={`${sig.name || sig.email} signature`}
                    className="h-7 w-16 object-contain border border-slate-200 rounded bg-white flex-shrink-0"
                    data-testid={`apply-signature-thumb-${sig.email}`}
                  />
                ) : (
                  <span className="h-7 w-16 flex-shrink-0" />
                )}
                <span className="flex flex-col min-w-0">
                  <span className="text-sm truncate">{sig.name || sig.email}</span>
                  <span className="text-xs text-slate-400 truncate">{sig.email}</span>
                </span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Applied-signature preview (visual confirmation, above "Yours sincerely") */}
      {appliedSig && (
        <span className="flex items-center gap-2 text-xs text-slate-500" data-testid="applied-signature-preview">
          {appliedSig.signature_data && (
            <img
              src={appliedSig.signature_data}
              alt={`${appliedSig.name} signature`}
              className="h-7 max-w-[140px] object-contain border border-slate-200 rounded bg-white"
            />
          )}
        </span>
      )}

      {/* Send to Client for Signing — BM-H474: not offered at all once the
          client has signed. */}
      {!clientSigned && (
        <Button
          size="sm"
          onClick={() => setModalOpen(true)}
          disabled={!appliedSig}
          className="bg-teal hover:bg-teal-hover text-white gap-2"
          data-testid="send-to-client-btn"
          title={!appliedSig ? "Apply a signature first" : "Send the CCL to the client for signing"}
        >
          <Send className="w-4 h-4" /> Send to Client for Signing
        </Button>
      )}

      {statusLabel()}

      {/* Send modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="send-to-client-modal">
          <DialogHeader>
            <DialogTitle>Send to client for signing</DialogTitle>
            <DialogDescription>
              The client receives a secure link to review and sign their Client Care Letter,
              and a link to download their welcome pack.
            </DialogDescription>
          </DialogHeader>
          {/* BM-H501 (11 Sep 2026): this one send now carries both. There is no
              longer a separate "Send care pack" button to remember. */}
          <p
            className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2"
            data-testid="welcome-pack-included-note"
          >
            This email includes the welcome pack download link for this matter. No separate
            care pack send is needed.
          </p>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="text-slate-600">To</span>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                data-testid="send-to-email"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Subject</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                data-testid="send-subject"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Greeting</span>
              <input
                type="text"
                value={salutation}
                onChange={(e) => setSalutation(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                data-testid="send-salutation"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Message</span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-sans"
                data-testid="send-body"
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              data-testid="send-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={sending || !toEmail}
              className="bg-teal hover:bg-teal-hover text-white gap-2"
              data-testid="send-confirm-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
